import React from 'react';
import { useSelector } from 'react-redux';

import { useTranslation, useExperiments } from '@wix/yoshi-flow-editor';
import { useRouter } from '@wix/tpa-router/react';

import { selectCanCreateEvents, selectHasAdminRole } from 'store/selectors';
import { useController } from 'common/context/controller/index';

import { Plus as PlusIcon } from '@wix/wix-ui-icons-common/on-stage';
import { Button, IButtonProps } from 'wui/Button';
import { Tooltip } from 'wui/Tooltip';

interface ICreateEventButtonProps extends IButtonProps {
  groupId: string;
}

export function CreateEventButton({
  groupId,
  onClick,
  ...buttonProps
}: ICreateEventButtonProps) {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const router = useRouter();
  const { verticals$ } = useController();
  const { hasEventsInstalled } = verticals$;

  const isAdmin = useSelector(selectHasAdminRole(groupId as string));
  let canCreateEvents = useSelector(selectCanCreateEvents(groupId));
  canCreateEvents =
    canCreateEvents && experiments.enabled('specs.groups.events-by-uou');

  if (!canCreateEvents) {
    return null;
  }

  return (
    <Tooltip
      disabled={hasEventsInstalled}
      content={
        isAdmin
          ? t('groups-web.events.disabled-tooltip.admin')
          : t('groups-web.events.disabled-tooltip.member')
      }
    >
      <Button
        onClick={navigateToEventCreation}
        outlined
        prefixIcon={<PlusIcon />}
        data-hook="create-event-button"
        disabled={!hasEventsInstalled}
        {...buttonProps}
      >
        {t('groups-web.events.create-new.label')}
      </Button>
    </Tooltip>
  );

  function navigateToEventCreation(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    onClick?.(e);
    router.go('group.events.create');
  }
}

CreateEventButton.displayName = 'CreateEventButton';
